import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Star } from "lucide-react";
import { toast } from "sonner";

export default function AdminHighlights() {
  const [highlights, setHighlights] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [picking, setPicking] = useState(false);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api.get("/highlights/all").then((r) => setHighlights(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
  };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const nextOrder = (highlights[highlights.length - 1]?.order || 0) + 1;
      await api.post("/highlights", { company_id: pick, order: nextOrder, active: true });
      toast.success("Destaque adicionado");
      setPick(""); setPicking(false); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover destaque?")) return;
    await api.delete(`/highlights/${id}`); toast.success("Removido"); refresh();
  };

  const move = async (h, dir) => {
    const newOrder = h.order + dir;
    await api.patch(`/highlights/${h.id}`, { company_id: h.company_id, order: newOrder, active: h.active });
    refresh();
  };

  const toggle = async (h) => {
    await api.patch(`/highlights/${h.id}`, { company_id: h.company_id, order: h.order, active: !h.active });
    refresh();
  };

  const used = new Set(highlights.map((h) => h.company_id));
  const available = companies.filter((c) => !used.has(c.id));
  const limit = 6;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Destaques da Home</h1>
          <p className="text-stone-500 text-sm mt-1">Selecione até <strong>6 empresas</strong> que aparecerão em destaque na página inicial.</p>
        </div>
        <a href="/admin" className="text-sm text-blue-900 hover:text-blue-950">← Voltar</a>
      </div>

      <Card className="p-5 mb-6 rounded-2xl border-stone-200">
        {!picking ? (
          <Button onClick={() => setPicking(true)} disabled={highlights.length >= limit} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="add-highlight-btn">
            <Plus size={14} className="mr-1.5"/> Adicionar destaque ({highlights.length}/{limit})
          </Button>
        ) : (
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[260px]">
              <Label>Empresa</Label>
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="pick-highlight"><SelectValue placeholder="Escolha"/></SelectTrigger>
                <SelectContent>
                  {available.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.city_slug} · {c.plan})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={add} disabled={!pick || busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="confirm-highlight">
              <Save size={14} className="mr-1.5"/> Adicionar
            </Button>
            <Button variant="outline" onClick={() => { setPicking(false); setPick(""); }}>Cancelar</Button>
          </div>
        )}
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {highlights.length === 0 && <p className="text-stone-500">Nenhum destaque ainda. Adicione até 6.</p>}
        {highlights.map((h, idx) => (
          <Card key={h.id} className={`overflow-hidden rounded-2xl border-2 ${h.active ? "border-amber-400" : "border-stone-300 opacity-50"}`} data-testid={`highlight-card-${h.id}`}>
            <div className="aspect-video bg-stone-100 relative">
              {h.company?.media?.[0]?.url ? (
                <img src={h.company.media[0].url} alt={h.company.name} className="absolute inset-0 w-full h-full object-cover"/>
              ) : h.company?.logo_url ? (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
                  <img src={h.company.logo_url} alt={h.company.name} className="h-20 w-20 object-cover rounded-2xl border-2 border-white shadow"/>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-stone-300 font-display text-5xl">{h.company?.name?.[0] || "?"}</div>
              )}
              <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-400 text-amber-950 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider">
                <Star size={11}/> #{idx + 1}
              </div>
            </div>
            <div className="p-3">
              <div className="font-display font-semibold text-stone-900 text-sm leading-tight truncate">{h.company?.name || "Empresa removida"}</div>
              <div className="text-[11px] text-stone-500 truncate">{h.company?.city_slug} · {h.company?.category}</div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex gap-1">
                  <button onClick={() => move(h, -1)} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500" aria-label="Subir"><ArrowUp size={14}/></button>
                  <button onClick={() => move(h, 1)} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500" aria-label="Descer"><ArrowDown size={14}/></button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggle(h)} className={`text-[11px] font-semibold px-2 py-1 rounded-md ${h.active ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-600"}`}>{h.active ? "Ativo" : "Inativo"}</button>
                  <button onClick={() => remove(h.id)} className="text-red-500 hover:text-red-700 p-1.5" data-testid={`remove-highlight-${h.id}`}><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
